import { BiMoon, BiSun } from "react-icons/bi";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast/headless";

import { useAuth } from "@/contexts/auth-context";
import { useTheme } from "@/hooks/theme";
import { Avatar, Dropdown, IconButton } from "@/ui";
import SignInButton from "@/app/nav-bar/components/sign-in-button";

function NavBar() {
  const { isLoggedIn, currentUser, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // The workout page brings its own compact header (per design 5A), so the
  // global nav is hidden there on mobile; desktop has room for both.
  const onWorkoutPage =
    matchPath("/workouts/:workoutId", location.pathname) !== null;
  // The logged-out landing ships its own nav.
  if (location.pathname === "/") return null;

  const signOut = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      toast.error("Failed to log out");
    }
  };

  return (
    <nav
      className={`${
        onWorkoutPage ? "hidden lg:flex" : "flex"
      } items-center justify-between px-4 sm:px-7 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] bg-surface-card border-b border-line-subtle not-prose`}
    >
      <button
        type="button"
        onClick={() => navigate(isLoggedIn ? "/home" : "/")}
        className="font-extrabold text-xl tracking-tight text-ink-primary cursor-pointer"
      >
        Lift Notes
      </button>
      <div className="flex items-center gap-1.5">
        <IconButton label="Toggle dark mode" onClick={toggle}>
          {isDark ? <BiSun size="1.35em" /> : <BiMoon size="1.35em" />}
        </IconButton>
        {isLoggedIn && currentUser ? (
          <Dropdown
            trigger={
              <button type="button" aria-label="Open user menu" className="block cursor-pointer">
                <Avatar
                  name={currentUser.displayName}
                  email={currentUser.email}
                  photoURL={currentUser.photoURL}
                />
              </button>
            }
            header={
              <div>
                {currentUser.displayName && (
                  <div className="text-sm font-semibold text-ink-primary">
                    {currentUser.displayName}
                  </div>
                )}
                <div className="text-sm text-ink-muted truncate">
                  {currentUser.email}
                </div>
              </div>
            }
            items={[
              { label: "Settings", onClick: () => navigate("/settings") },
              { label: "Leave feedback", onClick: () => navigate("/feedback") },
              { label: "Sign out", onClick: signOut },
            ]}
          />
        ) : (
          <SignInButton />
        )}
      </div>
    </nav>
  );
}

export default NavBar;
